import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { Info, Plus } from "lucide-react";
import { useEffect, useState } from "react";

import type { AppRouter } from "@runcheetah/api/router";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from "@runcheetah/ui";

import { CodeText, PageHeader, QueryErrorState, Textarea } from "@/components/common";
import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";

type RouterInputs = inferRouterInputs<AppRouter>;
type RouterOutputs = inferRouterOutputs<AppRouter>;
type CreditPack = RouterOutputs["admin"]["creditPacks"]["list"][number];
type CreditPackWriteInput = RouterInputs["admin"]["creditPacks"]["create"];

interface CreditPackFormState extends CreditPackWriteInput {
  id: string | null;
  creditsInput: string;
  expiresAfterDaysInput: string;
  priceInput: string;
}

const NEW_PACK_ID = "new";

const emptyPackForm = (): CreditPackFormState => ({
  id: null,
  code: "",
  name: "",
  description: null,
  active: false,
  credits: 100,
  creditsInput: "100",
  priceCents: 900,
  currency: "eur",
  expiresAfterDays: 90,
  expiresAfterDaysInput: "90",
  priceInput: "9.00",
});

const packToForm = (pack: CreditPack): CreditPackFormState => ({
  id: pack.id,
  code: pack.code,
  name: pack.name,
  description: pack.description,
  active: pack.active,
  credits: pack.credits,
  creditsInput: String(pack.credits),
  priceCents: pack.priceCents,
  currency: pack.currency,
  expiresAfterDays: pack.expiresAfterDays,
  expiresAfterDaysInput: String(pack.expiresAfterDays),
  priceInput: formatEuroPriceInput(pack.priceCents),
});

const parseIntegerInput = (value: string, minimum = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(minimum, Math.trunc(parsed)) : minimum;
};

const parseEuroPriceInput = (value: string): number => {
  const parsed = Number(value.trim().replace(",", "."));
  return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100)) : 0;
};

const formatEuroPriceInput = (value: number): string => (value / 100).toFixed(2);

function CreditPackList({
  packs,
  selectedId,
  showActiveOnly,
  onToggleShowActiveOnly,
  onSelect,
}: {
  packs: CreditPack[];
  selectedId: string;
  showActiveOnly: boolean;
  onToggleShowActiveOnly: (showActiveOnly: boolean) => void;
  onSelect: (id: string) => void;
}) {
  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">Run packs</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onToggleShowActiveOnly(!showActiveOnly)}
          >
            {showActiveOnly ? "Show all" : "Show active"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {showActiveOnly
            ? "Showing active packs only."
            : "Showing all packs. Active packs stay first."}
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        <button
          type="button"
          onClick={() => onSelect(NEW_PACK_ID)}
          className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition hover:bg-muted ${
            selectedId === NEW_PACK_ID ? "border-primary bg-primary/5" : "border-border"
          }`}
        >
          <span className="inline-flex items-center gap-2 font-medium">
            <Plus className="size-4" /> New run pack
          </span>
        </button>

        {packs.length ? null : (
          <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
            No run packs match this filter.
          </div>
        )}

        {packs.map((pack) => (
          <button
            key={pack.id}
            type="button"
            onClick={() => onSelect(pack.id)}
            className={`w-full rounded-lg border px-3 py-2 text-left transition hover:bg-muted ${
              selectedId === pack.id ? "border-primary bg-primary/5" : "border-border"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium">{pack.name}</span>
              <div className="flex items-center gap-1">
                <Badge variant="outline">v{pack.version}</Badge>
                <Badge variant={pack.active ? "default" : "outline"}>
                  {pack.active ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
            <CodeText className="mt-1 block">{pack.code}</CodeText>
            <div className="mt-2 text-xs text-muted-foreground">
              {pack.credits} runs · €{formatEuroPriceInput(pack.priceCents)} · expires in{" "}
              {pack.expiresAfterDays} days
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              Sync: {pack.stripeSyncStatus.replace("_", " ")}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {pack.purchaseCount}{" "}
              {pack.purchaseCount === 1 ? "workspace purchase" : "workspace purchases"}
            </div>
          </button>
        ))}
      </CardContent>
    </Card>
  );
}

function CreditPackEditor({
  form,
  selectedPack,
  isPending,
  errorMessage,
  onChange,
  onSave,
  onDelete,
}: {
  form: CreditPackFormState;
  selectedPack: CreditPack | null;
  isPending: boolean;
  errorMessage: string | null;
  onChange: (form: CreditPackFormState) => void;
  onSave: () => void;
  onDelete: () => void;
}) {
  const canDelete = selectedPack ? selectedPack.purchaseCount === 0 : false;
  const missingRequiredFields =
    !form.code.trim() ||
    !form.name.trim() ||
    !form.creditsInput.trim() ||
    !form.expiresAfterDaysInput.trim() ||
    !form.priceInput.trim();

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base">
            {form.id ? "Edit run pack" : "Create run pack"}
          </CardTitle>
          <Badge variant={form.active ? "default" : "outline"}>
            {form.active ? "Active" : "Inactive"}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Saving pricing, run count, expiry, or copy changes creates a new immutable pack version
          and syncs it to Stripe.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-2">
            <span className="text-sm font-medium">Code</span>
            <Input
              value={form.code}
              disabled={isPending}
              onChange={(event) => onChange({ ...form, code: event.target.value })}
              placeholder="boost-100"
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Name</span>
            <Input
              value={form.name}
              disabled={isPending}
              onChange={(event) => onChange({ ...form, name: event.target.value })}
              placeholder="100 Run Pack"
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Runs</span>
            <Input
              type="number"
              min={1}
              value={form.creditsInput}
              disabled={isPending}
              onChange={(event) => {
                const value = event.target.value;
                onChange({
                  ...form,
                  creditsInput: value,
                  credits: parseIntegerInput(value, 1),
                });
              }}
            />
          </label>
          <label className="space-y-2">
            <span className="text-sm font-medium">Expires after days</span>
            <Input
              type="number"
              min={1}
              value={form.expiresAfterDaysInput}
              disabled={isPending}
              onChange={(event) => {
                const value = event.target.value;
                onChange({
                  ...form,
                  expiresAfterDaysInput: value,
                  expiresAfterDays: parseIntegerInput(value, 1),
                });
              }}
            />
          </label>
        </div>

        <label className="space-y-2">
          <span className="text-sm font-medium">Description</span>
          <Textarea
            value={form.description ?? ""}
            disabled={isPending}
            onChange={(event) =>
              onChange({ ...form, description: event.target.value.trim() || null })
            }
            placeholder="Extra runs for busy campaign periods."
          />
        </label>

        <label className="flex items-start gap-3 rounded-lg border p-3">
          <input
            type="checkbox"
            className="mt-1 size-4"
            checked={form.active}
            disabled={isPending}
            onChange={(event) => onChange({ ...form, active: event.target.checked })}
          />
          <span className="space-y-1">
            <span className="block text-sm font-medium">Active run pack</span>
            <span className="block text-sm text-muted-foreground">
              Active, synced packs are available to subscribed workspaces in customer Billing.
            </span>
          </span>
        </label>

        <label className="space-y-2">
          <span className="flex items-center gap-2 text-sm font-medium">
            Price (€)
            <span aria-label="Tax information" title="Stripe handles VAT/tax during checkout.">
              <Info className="size-4 text-muted-foreground" />
            </span>
          </span>
          <Input
            inputMode="decimal"
            value={form.priceInput}
            disabled={isPending}
            onChange={(event) => {
              const value = event.target.value;
              onChange({ ...form, priceInput: value, priceCents: parseEuroPriceInput(value) });
            }}
            placeholder="9.00"
          />
        </label>

        <div className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
          Stripe products and one-time EUR prices are created automatically when you save. Price,
          run count, expiry, and copy changes create a new pack version.
        </div>

        {selectedPack ? (
          <div className="grid gap-3 rounded-lg border p-3 text-xs text-muted-foreground md:grid-cols-2">
            <div>
              <span className="block font-medium text-foreground">Product</span>
              <CodeText>{selectedPack.stripeProductId ?? "Not synced"}</CodeText>
            </div>
            <div>
              <span className="block font-medium text-foreground">Price</span>
              <CodeText>{selectedPack.stripePriceId ?? "None"}</CodeText>
            </div>
            {selectedPack.stripeSyncError ? (
              <div className="text-destructive md:col-span-2">{selectedPack.stripeSyncError}</div>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onSave} disabled={isPending || missingRequiredFields}>
            {isPending ? "Saving..." : form.id ? "Create new version" : "Save run pack"}
          </Button>
          {errorMessage ? <span className="text-sm text-destructive">{errorMessage}</span> : null}
        </div>

        {selectedPack ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
            <div className="space-y-1">
              <p className="text-sm font-medium">Delete run pack</p>
              <p className="text-sm text-muted-foreground">
                {canDelete
                  ? "This pack has no workspace purchases and can be removed."
                  : `This pack has ${selectedPack.purchaseCount} ${
                      selectedPack.purchaseCount === 1
                        ? "workspace purchase"
                        : "workspace purchases"
                    } and cannot be deleted.`}
              </p>
            </div>
            {canDelete ? (
              <Button variant="destructive" disabled={isPending} onClick={onDelete}>
                Delete run pack
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function CreditPacksPage() {
  const utils = trpc.useUtils();
  const { data, isPending, error } = trpc.admin.creditPacks.list.useQuery();
  const [selectedId, setSelectedId] = useState(NEW_PACK_ID);
  const [form, setForm] = useState<CreditPackFormState>(emptyPackForm);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const createPack = trpc.admin.creditPacks.create.useMutation({
    onSuccess: async (pack) => {
      await utils.admin.creditPacks.list.invalidate();
      setSelectedId(pack.id);
      setForm(packToForm(pack));
    },
  });

  const updatePack = trpc.admin.creditPacks.update.useMutation({
    onSuccess: async (pack) => {
      await utils.admin.creditPacks.list.invalidate();
      setSelectedId(pack.id);
      setForm(packToForm(pack));
    },
  });

  const deletePack = trpc.admin.creditPacks.delete.useMutation({
    onSuccess: async () => {
      await utils.admin.creditPacks.list.invalidate();
      setSelectedId(NEW_PACK_ID);
      setForm(emptyPackForm());
    },
  });

  const packs = data ?? [];
  const sortedPacks = packs.toSorted((left, right) => {
    if (left.active !== right.active) {
      return left.active ? -1 : 1;
    }

    return left.name.localeCompare(right.name) || right.version - left.version;
  });
  const visiblePacks = showActiveOnly ? sortedPacks.filter((pack) => pack.active) : sortedPacks;
  const selectedPack = packs.find((pack) => pack.id === selectedId) ?? null;
  const activePackCount = packs.filter((pack) => pack.active).length;

  useEffect(() => {
    if (selectedId === NEW_PACK_ID) {
      setForm(emptyPackForm());
      return;
    }

    if (selectedPack) {
      setForm(packToForm(selectedPack));
    }
  }, [selectedId, selectedPack]);

  if (isPending) {
    return (
      <div className="space-y-4">
        <PageHeader title="Run Packs" meta="Loading..." />
        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <Card className="h-80 animate-pulse bg-muted" />
          <Card className="h-96 animate-pulse bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <QueryErrorState
        title="Failed to load run packs"
        message={error.message}
        isForbidden={error.data?.code === "FORBIDDEN"}
      />
    );
  }

  const save = () => {
    const payload: CreditPackWriteInput = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description?.trim() || null,
      active: form.active,
      credits: parseIntegerInput(form.creditsInput, 1),
      priceCents: form.priceCents,
      currency: form.currency,
      expiresAfterDays: parseIntegerInput(form.expiresAfterDaysInput, 1),
    };

    if (form.id) {
      updatePack.mutate({ id: form.id, ...payload });
      return;
    }

    createPack.mutate(payload);
  };

  const deleteSelectedPack = () => {
    if (!form.id) {
      return;
    }

    deletePack.mutate({ id: form.id });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Run Packs"
        meta={`${activePackCount} active`}
        description="Configure customer-facing extra run packs and Stripe one-time prices."
      />

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <CreditPackList
          packs={visiblePacks}
          selectedId={selectedId}
          showActiveOnly={showActiveOnly}
          onToggleShowActiveOnly={setShowActiveOnly}
          onSelect={setSelectedId}
        />
        <CreditPackEditor
          form={form}
          selectedPack={selectedPack}
          isPending={createPack.isPending || updatePack.isPending || deletePack.isPending}
          errorMessage={
            createPack.error?.message ??
            updatePack.error?.message ??
            deletePack.error?.message ??
            null
          }
          onChange={setForm}
          onSave={save}
          onDelete={deleteSelectedPack}
        />
      </div>

      <div className="text-xs text-muted-foreground">
        Last updated: {packs[0] ? formatDateTime(packs[0].updatedAt) : "No packs yet"}
      </div>
    </div>
  );
}
